<template>
  <div id="app">
    <b-container>
      <b-row>
        <b-col lg="10" xl="8">
          <b-card
            title="Generate Activation Code"
            class="mt-3"
            header-tag="header"
            variant="primary"
          >
            <p class="text-muted mb-3">
              Select transaction type and period. The activation code is
              generated automatically for Repbase. Purchase date is set to the current date/time
              and party is kept empty.
            </p>
            <b-form @submit.prevent="onSubmit">
              <b-form-group label-class="font-weight-bold">
                <b-row>
                  <b-col md="6">
                    <b-form-group
                      v-if="formData.transactionType !== 'create_teaching'"
                      label="Period"
                    >
                      <b-form-select
                        v-model="formData.period"
                        :options="periodOptions"
                        required
                      ></b-form-select>
                    </b-form-group>
                    <b-form-group
                      v-else
                      label="Expiration Date"
                      label-class="font-weight-bold"
                    >
                      <b-form-input
                        type="date"
                        v-model="formData.expirationDate"
                        :min="minExpirationDate"
                        :max="maxExpirationDate"
                        required
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Transaction Type"
                      label-class="font-weight-bold"
                    >
                      <b-form-select
                        v-model="formData.transactionType"
                        :options="transactionTypeOptions"
                        required
                      ></b-form-select>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-form-group>

              <b-button type="submit" variant="primary" :disabled="submitting">
                <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
                {{ submitting ? "Generating..." : "Generate Code" }}
              </b-button>
              <b-button
                type="button"
                variant="outline-secondary"
                class="ml-2"
                @click="resetForm"
              >
                Reset
              </b-button>
            </b-form>

            <div v-if="generatedCode" class="generated-box mt-4">
              <label class="font-weight-bold mb-2">Generated Activation Code</label>
              <b-input-group>
                <b-form-input :value="generatedCode" readonly></b-form-input>
                <b-input-group-append>
                  <b-button variant="success" @click="copyGeneratedCode">
                    <i class="fa fa-copy"></i> Copy
                  </b-button>
                </b-input-group-append>
              </b-input-group>
              <div class="text-muted mt-2 small">
                Partner: {{ generatedMeta.partnerId || "—" }} |
                Period: {{ generatedMeta.period || "—" }} days |
                Transaction: {{ generatedMeta.transactionType || "—" }}
              </div>
            </div>

            <b-alert
              :show="showNotification"
              :variant="notificationVariant"
              class="mt-3"
            >
              {{ notificationMessage }}
            </b-alert>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
export default {
  data() {
    return {
      formData: {
        period: 365,
        partnerId: "repbase",
        transactionType: null,
        expirationDate: null,
      },
      periodOptions: [
        { text: "Select Period", value: null, disabled: true },
        { text: "Month", value: 30 },
        { text: "Year", value: 365 },
      ],
      transactionTypeOptions: [
        { text: "Select Transaction Type", value: null, disabled: true },
        { text: "create", value: "create" },
        { text: "create_free", value: "create_free" },
        { text: "create_teaching", value: "create_teaching" },
        { text: "renew", value: "renew" },
      ],
      submitting: false,
      generatedCode: "",
      generatedMeta: {
        partnerId: "",
        period: null,
        transactionType: "",
      },
      showNotification: false,
      notificationVariant: "success",
      notificationMessage: "",
    };
  },
  computed: {
    minExpirationDate() {
      return new Date().toISOString().slice(0, 10);
    },
    maxExpirationDate() {
      const d = new Date();
      d.setFullYear(d.getFullYear() + 2);
      return d.toISOString().slice(0, 10);
    },
  },
  methods: {
    resetForm() {
      this.formData.period = 365;
      this.formData.partnerId = "repbase";
      this.formData.transactionType = null;
      this.formData.expirationDate = null;
      this.generatedCode = "";
      this.generatedMeta = {
        partnerId: "",
        period: null,
        transactionType: "",
      };
      this.showNotification = false;
    },
    copyGeneratedCode() {
      if (!this.generatedCode) return;
      navigator.clipboard.writeText(this.generatedCode).then(() => {
        this.showNotification = true;
        this.notificationVariant = "success";
        this.notificationMessage = "Activation code copied to clipboard.";
      });
    },
    async onSubmit() {
      this.submitting = true;
      this.$store
        .dispatch("activation/create", this.formData)
        .then((res) => {
          const isSuccess = res && res.data && res.data.success;
          if (!isSuccess) {
            this.showNotification = true;
            this.notificationVariant = "danger";
            this.notificationMessage =
              (res && res.data && res.data.message) ||
              "Unable to generate activation code.";
            this.submitting = false;
            return;
          }

          const generatedCodeValue =
            res &&
            res.data &&
            res.data.activationCode &&
            res.data.activationCode.activationCode;
          this.generatedCode = generatedCodeValue || "";
          this.generatedMeta = {
            partnerId:
              res &&
              res.data &&
              res.data.activationCode &&
              res.data.activationCode.partnerId,
            period:
              res &&
              res.data &&
              res.data.activationCode &&
              res.data.activationCode.period,
            transactionType:
              res &&
              res.data &&
              res.data.activationCode &&
              res.data.activationCode.transactionType,
          };

          this.showNotification = true;
          this.notificationVariant = "success";
          if (this.generatedCode) {
            this.notificationMessage = "Activation code generated successfully.";
          } else {
            this.notificationMessage = "Activation code generated successfully!";
          }
          this.submitting = false;
        })
        .catch((err) => {
          this.showNotification = true;
          this.notificationVariant = "danger";
          this.notificationMessage =
            "An error occurred while submitting the activation code.";
          this.submitting = false;
        });
    },
  },
};
</script>

<style scoped>
.generated-box {
  border: 1px solid #d2e9d4;
  background: #f5fff6;
  border-radius: 6px;
  padding: 12px;
}
</style>



// WEBPACK FOOTER //
// src/views/activation/ActivationAdd.vue